import { useCallback, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ConfigContext } from "../../../../utils/ConfigContext";
import { UpdateType } from "../EditorContextProvider";
import {
  PlatformBody,
  PlatformSection,
  PlatformSectionItem,
  PlatformSectionItemBody,
  PlatformTag,
} from "./Platform-styled";

import { useDispatch, useSelector } from "react-redux";
import { setEditorUpdateType } from "../../../../redux/action/Dashboard-action";
import { getLayerReports } from "../../../../utils/helper";
import LayerIcon from "../../../Icons/LayerIcon";
import { Tooltip } from "../../../UI";

const baseLineColor = "#34495e";

export const EntryPoints = ["aerodromes", "eep", "ports"];

const positions = {
  0: { top: -1, left: 0, zIndex: 1 },
  1: { top: -1, right: -2 },
  2: { top: 10, left: 5 },
};

const capitalizeFirstLetter = (string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

const Platform = ({
  entryPointsLayers,
  layers,
  onLayerSelect,
  updateType,
  reportsLayerMap,
  allReports,
  reportColors,
  children,
}) => {
  const { layersUpdates } = useSelector((state) => state.cycleManagerReducer);
  const activeModule = useSelector((state) => state.activeModule);
  const reportsLayer = useSelector((state) => state.dashboard.reportsLayer);

  const { config } = useContext(ConfigContext);
  const { t } = useTranslation("common");
  const dispatch = useDispatch();

  const handleSelection = useCallback(
    (layer) => {
      dispatch(setEditorUpdateType(updateType));
      if (onLayerSelect) {
        onLayerSelect(layer);
      }
    },
    [onLayerSelect, updateType]
  );

  const color = useMemo(() => {
    return UpdateType.baseline === updateType ? baseLineColor : config.opsColor;
  }, [config, updateType]);

  const filteredLayers = useMemo(() => {
    const entryPointsSet = new Set(entryPointsLayers);
    const newLayers = [...layers];
    const configLayers = config.layers || [];

    const { layers: moduleLayers = [], optionalLayers = [] } =
      config.modules[activeModule] || {};

    const layersToShow = newLayers.filter(
      (l) =>
        moduleLayers.includes(l.layerConfig.alias) ||
        optionalLayers.includes(l.layerConfig.alias) ||
        configLayers.includes(l.layerConfig.alias)
    );

    if (updateType === UpdateType.baseline) {
      return [...layersToShow].reverse();
    } else {
      return layersToShow;
    }
  }, [layers, updateType, activeModule, config]);

  const title = useMemo(() => {
    return updateType;
  }, []);

  const entryPointLayersTitle = useMemo(() => {
    if (updateType === UpdateType.baseline) {
      return "Edit Entry points";
    }

    const entryPointsTotal = entryPointsLayers.reduce((acc, layer) => {
      const { percent = 0 } = layersUpdates[layer.layerConfig.id] || {};
      return acc + percent;
    }, 0);

    return `${entryPointsTotal}% ${t("cycleManager.status.upToDate.title")}`;
  }, [entryPointsLayers, layersUpdates, updateType]);

  return (
    <>
      <PlatformBody>
        {(entryPointsLayers.length > 0 || layers.length > 0) && (
          <PlatformSection>
            {children}
            {filteredLayers.map((layer) => {
              const { percent = 0 } = layersUpdates[layer.layerConfig.id] || {};

              let reportsArr = [1];
              if (Object.keys(reportsLayerMap || {}).length > 0) {
                reportsArr = reportsArr.map((field) => {
                  const reports = getLayerReports(layer, reportsLayerMap);
                  let count = Array.isArray(reports)
                    ? reports.filter(
                        (f) => f.attributes.currvalidationfield === field
                      ).length
                    : 0;

                  if (
                    layer.layerConfig.id === reportsLayer.layerConfig.id &&
                    Array.isArray(allReports)
                  ) {
                    count += allReports.filter(
                      (f) =>
                        f.attributes.reporttype === 1 &&
                        f.attributes.currvalidationfield === 1
                    ).length;
                  }
                  return {
                    color: reportColors[field] || "#000",
                    count: count,
                    field,
                  };
                });
              }

              return (
                <PlatformSectionItem
                  key={layer.id}
                  id={layer.id}
                  onClick={() => handleSelection(layer)}
                >
                  <Tooltip
                    wrapperStyles={{
                      position: "absolute",
                      width: "100%",
                      height: "100%",
                      left: 0,
                      top: 0,
                    }}
                    blurred
                    showOnHover
                    content={layer.getLayerInformation(t)}
                  />
                  <LayerIcon
                    config={config}
                    layer={layer}
                    t={t}
                    width={34}
                    height={34}
                    textColor="rgb(73, 73, 73)"
                    opsColor="rgb(73, 73, 73)"
                    fill="rgb(73, 73, 73)"
                    circleColor="#ffffff"
                    wrapperStyles={{
                      background: "#ffffff",
                      borderRadius: 100,
                    }}
                    textStyles={{
                      background: "#ffffff",
                      borderRadius: 100,
                      height: 34,
                      width: 34,
                      // fontSize: 24,
                    }}
                  />

                  <PlatformSectionItemBody>
                    <span className="title text-elipsis-2">
                      {/* {UpdateType.baseline === updateType
                        ? t("screen.widget.common.edit")
                        : t("screen.widget.common.update")}{" "} */}
                      {layer.getLayerTitle(t)}
                    </span>

                    {reportsArr
                      .filter((report) => report?.count > 0)
                      .map((reports, index) => {
                        const { color, count = 0 } = reports || {};
                        if (count === 0) return null;
                        return (
                          <PlatformTag
                            key={index}
                            style={{
                              textTransform: "lowercase",
                              background: color,
                              color: "#fff",
                              minWidth: "16px",
                              width: "16px",
                              height: "16px",
                              boxSizing: "border-box",
                              margin: 0,
                              fontSize: "10px",
                            }}
                            position="absolute"
                            right={10 + index * 18 + "px"}
                            top="-8px"
                          >
                            {count}
                          </PlatformTag>
                        );
                      })}
                  </PlatformSectionItemBody>
                </PlatformSectionItem>
              );
            })}
          </PlatformSection>
        )}
      </PlatformBody>
    </>
  );
};

export default Platform;
